/**
 * @file CollapsibleTierHeaderContainer.tsx
 * @description Container that binds the tier name to the toggle callback,
 * producing a zero-argument onPress for the view. This avoids creating
 * inline arrow functions in the parent's JSX map.
 * @module features/squad-planning/components/CollapsibleTierHeader
 */

import type { ReactNode } from "react";
import { memo, useCallback } from "react";

import CollapsibleTierHeaderView, {
  type CollapsibleTierHeaderAppearance,
} from "./CollapsibleTierHeaderView";

/** Props for {@link CollapsibleTierHeaderContainer} */
export interface CollapsibleTierHeaderContainerProps {
  /** Display name of the tier */
  readonly tierName: string;
  /** Number of players in this tier */
  readonly count: number;
  /** Whether this tier is currently collapsed */
  readonly isCollapsed: boolean;
  /** Parent callback that receives the tier name to toggle */
  readonly onToggleTier: (tierName: string) => void;
  /** Optional action element rendered on the right side of the header (e.g. CTA button) */
  readonly actionSlot?: ReactNode;
  /** Outline and label treatment (SE-8486); `standalone` when omitted. */
  readonly appearance?: CollapsibleTierHeaderAppearance;
}

/**
 * Binds tierName into the toggle callback so the view receives a stable
 * zero-argument onPress handler. Passes through an optional actionSlot
 * for tier-specific CTA buttons (e.g. "+ Academy Player").
 * @param props - Container props
 * @param props.tierName - Tier display label, also used as the toggle key
 * @param props.count - Number of players in this tier
 * @param props.isCollapsed - Whether the tier body is hidden
 * @param props.onToggleTier - Parent callback accepting the tier name
 * @param props.actionSlot - Optional action element for the right side of the header
 * @param props.appearance - Outline and label treatment
 * @returns The CollapsibleTierHeaderView with a bound press handler
 */
const CollapsibleTierHeaderContainer = ({
  tierName,
  count,
  isCollapsed,
  onToggleTier,
  actionSlot,
  appearance,
}: CollapsibleTierHeaderContainerProps) => {
  const handlePress = useCallback(
    () => onToggleTier(tierName),
    [onToggleTier, tierName]
  );

  return (
    <CollapsibleTierHeaderView
      tierName={tierName}
      count={count}
      isCollapsed={isCollapsed}
      onPress={handlePress}
      actionSlot={actionSlot}
      appearance={appearance}
    />
  );
};

CollapsibleTierHeaderContainer.displayName = "CollapsibleTierHeaderContainer";

export default memo(CollapsibleTierHeaderContainer);
